import { useRef, useState } from "react";
import { Form, Link, redirect, useActionData, useNavigation, useParams, useSearchParams, useSubmit } from "react-router";
import type { ActionFunctionArgs, LoaderFunctionArgs } from "react-router";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { ApiError, type PartConflict } from "@repo/api-client";
import type { EventPart } from "@repo/shared-types";
import { api } from "@/lib/api";
import { errorMessage, formError } from "@/lib/session";
import { partLabel, readEventChanges } from "@/lib/event-form";
import { applyEventImage, loadSpaceContext, useSpaceContext } from "@/lib/events";
import { queries } from "@/lib/queries";
import { read, readFresh } from "@/lib/query";
import { tenantPath } from "@/lib/tenants";
import { formatMoment, viewerZone } from "@/lib/times";
import { ErrorNote, ProblemProvider, useFormProblem } from "@/components/form";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { EventFields } from "@/components/event-fields";
import { EventImageField } from "@/components/event-image";
import { BackLink } from "@/components/navigation";
import { PageTitle } from "@/components/brand";
import { Modal } from "@/components/modal";

/**
 * Editing an event's own fields, whether it is published and, where the
 * deployment keeps pictures, its image. Its parts are edited on the event's
 * page, one at a time.
 */

export async function editEventLoader({ request, params }: LoaderFunctionArgs) {
  const slug = params.slug ?? "";
  // The form sends the whole event back: it starts from a fresh read.
  const [context] = await Promise.all([
    loadSpaceContext(request, slug),
    readFresh(queries.event(slug, params.event ?? "")),
  ]);
  if (context.canManageEvents) await read(queries.eventOptions());
  return null;
}

export async function editEventAction({ request, params }: ActionFunctionArgs) {
  const form = await request.formData();
  const slug = params.slug ?? "";
  const eventId = params.event ?? "";
  try {
    const [profile, options] = await Promise.all([api.me.profile.get(), api.eventOptions()]);
    const read = readEventChanges(form, viewerZone(profile.timeZone).zone, options);
    if (read.error !== undefined) return { error: read.error, field: read.field };
    await api.space(slug).events.update(eventId, read.value);
    try {
      await applyEventImage(slug, eventId, form);
    } catch (error) {
      return { error: `${errorMessage(error, "The image didn't go up.")} Everything else is saved.` };
    }
    // No toast: the event's page, where this goes, shows what was saved.
    return redirect(`/t/${slug}/events/${eventId}`);
  } catch (error) {
    // New dates that leave parts outside: the page lists them, and offers to
    // send the same save again fitting them (0083).
    if (error instanceof ApiError && error.status === 409 && error.conflicts?.length) {
      return { conflicts: error.conflicts };
    }
    return formError(error, "Could not save the event");
  }
}

/** What saving the event answered, when it did not go to the event's page. */
type EditOutcome = { error?: string; field?: string; conflicts?: PartConflict[] };

export default function EditEvent() {
  const params = useParams();
  const slug = params.slug ?? "";
  const { session, tenant, canManageEvents, zone } = useSpaceContext(slug);
  const { data: event } = useSuspenseQuery(queries.event(slug, params.event ?? ""));
  // Read by the loader for those who may edit; nobody else asks.
  const options = useQuery({ ...queries.eventOptions(), enabled: canManageEvents }).data ?? null;
  // Arrived from posting it, whose image did not go up.
  const imageFailed = useSearchParams()[0].get("image") === "failed";
  const result = useActionData() as EditOutcome | undefined;
  const navigation = useNavigation();
  const back = `${tenantPath(tenant)}/events/${event.id}`;
  const form = useRef<HTMLFormElement>(null);
  const shown = useFormProblem(result, form);
  // The parts the new dates leave outside, until Cancel lets that answer go.
  const [dismissed, setDismissed] = useState<EditOutcome | undefined>(undefined);
  const conflicts = result?.conflicts && result !== dismissed ? result.conflicts : null;
  const submit = useSubmit();
  const encType = session.images ? "multipart/form-data" : undefined;
  const fitParts = () => {
    if (!form.current) return;
    const data = new FormData(form.current);
    data.set("fitParts", "true");
    void submit(data, { method: "post", encType });
  };

  return (
    <section className="space-y-6">
      <div className="space-y-2">
        <BackLink to={back}>{event.title}</BackLink>
        <PageTitle of={["Edit event", event.title]} />
        <h1 className="page-title">Edit event</h1>
      </div>

      {!canManageEvents || !options ? (
        <Alert role="note">
          <AlertDescription>Only the owner, and admins the owner has given Manage events, can edit events here.</AlertDescription>
        </Alert>
      ) : (
        <Form
          ref={form}
          method="post"
          encType={encType}
          className="space-y-6"
          noValidate
          onChange={shown.onChange}
        >
          {result?.error ? (
            shown.general && <ErrorNote message={shown.general} />
          ) : (
            imageFailed && (
              <ErrorNote message="The event is posted, but its image didn't go up. Choose it again here — a JPEG or PNG." />
            )
          )}
          <Card>
            <CardContent>
              <ProblemProvider value={shown.problem}>
                <EventFields
                  options={options}
                  zone={zone}
                  event={event}
                  image={session.images ? <EventImageField current={event.image} /> : undefined}
                />
              </ProblemProvider>
            </CardContent>
          </Card>
          <div className="flex justify-end gap-2">
            <Button asChild variant="outline" size="lg">
              <Link to={back}>Cancel</Link>
            </Button>
            <Button type="submit" size="lg" disabled={navigation.state === "submitting"}>
              Save
            </Button>
          </div>
        </Form>
      )}
      {conflicts && (
        <FitPartsSheet
          conflicts={conflicts}
          parts={event.parts}
          zone={zone}
          busy={navigation.state === "submitting"}
          onFit={fitParts}
          onClose={() => setDismissed(result)}
        />
      )}
    </section>
  );
}

/**
 * The parts an event's new dates leave outside, one line each with what
 * fitting does to it — "B. Metcon: starts Mon 2 Nov · 09:00 → with the
 * event" — and the way out: Fit the parts sends the same save again, moving
 * them inside with the event in one go, and Cancel goes back to the form.
 */
function FitPartsSheet({
  conflicts,
  parts,
  zone,
  busy,
  onFit,
  onClose,
}: {
  conflicts: PartConflict[];
  parts: EventPart[];
  zone: string;
  busy: boolean;
  onFit: () => void;
  onClose: () => void;
}) {
  // One line a part, in the event's order, whichever of its dates are outside.
  const lines = parts.flatMap((part, index) => {
    const mine = conflicts.filter((conflict) => conflict.partId === part.id);
    if (mine.length === 0) return [];
    const changes = mine.map((conflict) =>
      conflict.field === "startsAt"
        ? `starts ${formatMoment(conflict.from, zone)} → ${conflict.to ? formatMoment(conflict.to, zone) : "with the event"}`
        : `due ${formatMoment(conflict.from, zone)} → ${conflict.to ? formatMoment(conflict.to, zone) : "no deadline"}`,
    );
    return [{ id: part.id, text: `${partLabel(index)}. ${part.title}: ${changes.join(", ")}` }];
  });
  const title = lines.length === 1 ? "1 part falls outside the new dates" : `${lines.length} parts fall outside the new dates`;
  return (
    <Modal title={title} onClose={onClose}>
      <ul className="space-y-2 text-sm">
        {lines.map((line) => (
          <li key={line.id}>{line.text}</li>
        ))}
      </ul>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" size="lg" onClick={onClose}>
          Cancel
        </Button>
        <Button type="button" size="lg" disabled={busy} onClick={onFit}>
          Fit the parts
        </Button>
      </div>
    </Modal>
  );
}
